import { NgStyle } from '@angular/common';
import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { startWith } from 'rxjs/operators';

import { CustomValidators } from '@shared/components/base-form-control/custom-validators';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef, ModalAction } from '@shared/components/overlay/custom-overlay-ref';

import { ModalComponent } from '../modal/modal.component';

export interface ConfirmationActionModalData {
  executeActionText?: string;
  hideConfirmationHint?: boolean;
  keywordToExecuteAction?: string;
  message?: string;
  showNotesSection?: {
    documentsConfig: {
      path: string;
      required: boolean;
    };
    noteConfig: {
      required: boolean;
    };
  };
  skipKeywordConfirmation?: boolean;
  width?: string;
}

@Component({
  imports: [
    ModalComponent,
    InputComponent,
    ReactiveFormsModule,
    NgStyle,
    FileManagerComponent,
    FileViewerComponent,
    IconComponent,
    FormsModule,
  ],
  template: `
    @let config = ref.data;
    @if (config) {
      <aux-modal
        [primaryButtonDisabled]="primaryButtonDisabled()"
        [primaryButtonAction]="primaryAction"
        [primaryButtonTooltip]="primaryButtonTooltip()"
      >
        <form
          class="w-[520px]"
          [ngStyle]="{ width: config.width }"
          (keydown.enter)="$event.preventDefault()"
        >
          <div [innerHTML]="config.message"></div>

          @if (!config.hideConfirmationHint) {
            <p class="mt-6 font-medium italic" [class.mb-6]="!!config.skipKeywordConfirmation">
              @if (config.executeActionText) {
                {{ config.executeActionText }}
              } @else {
                To prevent accidental changes, please type “{{ config.keywordToExecuteAction }}” in
                the field below to confirm.
              }
            </p>
          }

          @if (!config.skipKeywordConfirmation) {
            <aux-input
              [label]="config.keywordToExecuteAction ?? ''"
              [placeholder]="config.keywordToExecuteAction ?? ''"
              class="mt-4 text-xs"
              validators="required"
              [formControl]="confirmationFc"
            />
          }

          @if (config.showNotesSection) {
            @let isRequired = config.showNotesSection.noteConfig.required;
            <div class="mt-4 grid grid-cols-2">
              <div class="mr-8 flex flex-col items-start justify-start space-y-3">
                <div class="flex items-center justify-start space-x-1">
                  <aux-icon [name]="'Message'" />
                  <span class="font-bold">
                    <span class="mr-1">Notes</span>
                    @if (isRequired) {
                      <span class="text-aux-error">*</span>
                    }
                  </span>
                </div>
                <aux-input
                  class="w-full pl-1 text-xs"
                  placeholder="Enter notes here..."
                  [validators]="isRequired ? 'required' : ''"
                  [inAppBudgetStyle]="true"
                  [textArea]="true"
                  resize="none"
                  errMessage="Notes*"
                  [(ngModel)]="notes"
                  [ngModelOptions]="{ standalone: true }"
                />
              </div>
              <div class="flex w-full flex-col space-y-3">
                <div class="flex flex-row items-center justify-start space-x-2">
                  <span class="font-bold">Supporting Documents</span>
                  <span class="italic">(optional)</span>
                </div>
                <div class="w-full">
                  <aux-file-manager
                    #manager
                    class="h-32"
                    [fetchFilesOnInit]="false"
                    [eager]="false"
                    [path]="config.showNotesSection.documentsConfig.path"
                  />
                  <div class="mt-4 max-h-[120px] overflow-y-auto">
                    <aux-file-viewer
                      [fileManager]="manager"
                      [disableFirstFileMargin]="true"
                      [onlyShowUploaded]="false"
                    />
                  </div>
                </div>
              </div>
            </div>
          }
        </form>
      </aux-modal>
    }
  `,
})
export class ConfirmationActionModalComponent {
  ref = inject(CustomOverlayRef<boolean, ConfirmationActionModalData>);

  notes = signal('');

  confirmationFc = new FormControl(null, [
    Validators.required,
    CustomValidators.mustEqualWith(this.ref.data?.keywordToExecuteAction ?? ''),
  ]);

  confirmationStatus = toSignal(
    this.confirmationFc.statusChanges.pipe(startWith(this.confirmationFc.status)),
    {
      requireSync: true,
    }
  );

  primaryButtonDisabled = computed(() => {
    const status = this.confirmationStatus();
    const notes = this.notes();
    const refData = this.ref.data;

    if (refData?.showNotesSection) {
      const isNotesRequired = refData.showNotesSection.noteConfig.required;

      if (isNotesRequired && !notes) {
        return true;
      }
    }

    if (refData?.skipKeywordConfirmation) {
      return false;
    }

    return status === 'INVALID';
  });
  primaryButtonTooltip = computed(() => {
    if (this.primaryButtonDisabled()) {
      return 'Enter required fields to save.';
    }

    return '';
  });

  fileManager = viewChild(FileManagerComponent);

  // weird hack to send this instance to the action
  // we need to refactor aux modal later
  primaryAction: ModalAction<ModalComponent> = async (instance: ModalComponent | null) => {
    const action = instance?.ref?.settings?.primaryButton?.action;

    if (action) {
      await action(this);
    }
  };
}
